import { useTheme } from "@emotion/react";
import { Close } from "@mui/icons-material";
import {
  Box,
  Button,
  Container,
  Divider,
  Fade,
  FormControlLabel,
  FormGroup,
  Grid,
  Modal,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  resetRentalItemFilter,
  setRentalItemFilter,
} from "../../stateManagement/reducers/rentalItemsReducer";
import { RentalItemSortBy } from "../../types/RentalItemSortBy";
import AnimateButton from "../@extended/AnimateButton";
import MDBox from "../MDBox";
import MDTypography from "../MDTypography";

const RentalItemFilterModal = ({
  isModalOpen,
  openModal,
  showOrderPlacementModal,
}) => {
  const toggleModal = () => {
    openModal(!isModalOpen);
  };
  const { authToken, user } = useSelector((state) => state.auth);
  const { cart } = useSelector((state) => state.cart);
  const { rentalItems, rentalItemFilter } = useSelector(
    (state) => state.rentalItems,
  );

  const dispatch = useDispatch();

  const theme = useTheme();
  const idGreaterThanMd = useMediaQuery(theme.breakpoints.up("md"));

  //undefined | RentalItemSortBy
  const [sortOrder, setSortOrder] = useState(rentalItemFilter.sortBy);

  //undefined | number
  const [minPrice, setMinPrice] = useState(rentalItemFilter?.priceRange?.min);
  const [maxPrice, setMaxPrice] = useState(rentalItemFilter?.priceRange?.max);

  const setFilter = () => {
    if (minPrice && maxPrice && parseInt(maxPrice) < parseInt(minPrice)) {
      alert("Min price must be lesser than max price");
      return;
    }

    dispatch(
      setRentalItemFilter({
        sortBy: sortOrder,
        ...((minPrice !== undefined || maxPrice !== undefined) && {
          priceRange: { min: minPrice, max: maxPrice },
        }),
      }),
    );

    openModal(false);
  };

  const sortOptions = [
    {
      name: RentalItemSortBy.A_Z,
      label: "A - Z",
    },
    {
      name: RentalItemSortBy.Z_A,
      label: "Z - A",
    },
    {
      name: RentalItemSortBy.PRICE_ASC,
      label: "Min Price - Max Price",
    },
    {
      name: RentalItemSortBy.PRICE_DESC,
      label: "Max Price - Min Price",
    },
  ];

  return (
    <>
      <MDBox component="section" py={6} px={2}>
        <Container>
          <Modal
            open={isModalOpen}
            onClose={toggleModal}
            sx={{
              // display: "grid",
              placeItems: "flex-end",
              maxHeight: "100vh !important",
              overflowY: "auto !important",
              p: "1rem !important",
            }}
          >
            <Fade in={isModalOpen} timeout={500}>
              <MDBox
                sx={{
                  maxWidth: "500px",
                  width: "100%",
                  backgroundColor: "#faf9f6b8 !important",
                  backdropFilter: "blur(4px) saturate(140%)",
                  mb: "auto",
                  mt: "7rem",
                  mr: "1rem",
                }}
                position="relative"
                display="flex"
                flexDirection="column"
                borderRadius="xl"
                shadow="xl"
              >
                <MDBox
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  p={2}
                >
                  <MDTypography
                    variant="h5"
                    sx={{
                      color: "primary.main",
                      fontSize: "1.875rem",
                      lineHeight: "1",
                    }}
                  >
                    Filter
                  </MDTypography>
                  <AnimateButton>
                    <Close
                      fontSize="medium"
                      color="error"
                      sx={{ cursor: "pointer" }}
                      onClick={toggleModal}
                    />
                  </AnimateButton>
                </MDBox>
                <Divider
                  textAlign="center"
                  sx={{
                    mx: "auto",
                    my: 0,
                    width: "90%",
                    backgroundColor: `#e3e3de99`,
                  }}
                />
                <Box
                  sx={{
                    p: 3,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  {/* Section 1: Sort By (A-Z / Z-A) */}
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, color: "text.primary", mb: 1.5 }}
                    >
                      Sort By
                    </Typography>
                    <FormGroup>
                      <Grid container spacing={1}>
                        {sortOptions.map((option, i) => {
                          return (
                            <Grid item xs="auto" justifyContent="flex-start">
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={sortOrder === option.name}
                                    color="primary"
                                  />
                                }
                                label={
                                  <Typography
                                    sx={{
                                      display: "inline-block",
                                      color: "primary.main",
                                    }}
                                  >
                                    {option.label}
                                  </Typography>
                                }
                                onChange={() => {
                                  if (sortOrder === option.name) {
                                    setSortOrder(undefined);
                                  } else {
                                    setSortOrder(option.name);
                                  }
                                }}
                              />
                            </Grid>
                          );
                        })}
                      </Grid>
                    </FormGroup>
                  </Box>

                  <Divider />

                  {/* Section 2: Price Range */}
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, color: "text.primary", mb: 1.5 }}
                    >
                      Price Range (₦)
                    </Typography>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 2,
                      }}
                    >
                      <TextField
                        label="Min Price"
                        type="number"
                        size="small"
                        value={minPrice}
                        onChange={(e) => {
                          const price = parseInt(e.target.value);

                          if (isNaN(price) || price < 1) {
                            return;
                          }

                          setMinPrice(price);
                        }}
                        placeholder="0"
                        fullWidth
                      />
                      <TextField
                        label="Max Price"
                        type="number"
                        size="small"
                        value={maxPrice}
                        onChange={(e) => {
                          const price = parseInt(e.target.value);

                          if (isNaN(price) || price < 1) {
                            return;
                          }

                          setMaxPrice(price);
                        }}
                        placeholder="10000"
                        fullWidth
                      />
                    </Box>
                  </Box>
                </Box>
                <Divider
                  textAlign="center"
                  sx={{
                    mx: "auto",
                    my: 0,
                    width: "90%",
                    backgroundColor: `#e3e3de99`,
                  }}
                />
                <Grid
                  container
                  direction="row"
                  spacing="space-between"
                  sx={{ my: "2rem", px: "2rem" }}
                >
                  <Grid item xs={6}>
                    <AnimateButton>
                      <Button
                        variant="contained"
                        size={idGreaterThanMd ? "medium" : "small"}
                        color="info"
                        sx={{
                          background:
                            "linear-gradient(to top, #8b0000, #ff5e57) !important",
                          border: "none !important",
                        }}
                        onClick={() => {
                          openModal(false);
                          dispatch(resetRentalItemFilter());
                        }}
                      >
                        <b>Reset Filter</b>
                      </Button>
                    </AnimateButton>
                  </Grid>
                  <Grid item xs={6} sx={{ display: "flex" }}>
                    <Box component="span" sx={{ ml: "auto !important" }}>
                      <AnimateButton>
                        <Button
                          variant="contained"
                          size={idGreaterThanMd ? "medium" : "small"}
                          color="info"
                          sx={{
                            background:
                              "linear-gradient(to top,#2b6e3f, #4caf50) !important",
                            ml: "auto",
                            border: "none !important",
                          }}
                          onClick={() => {
                            setFilter();
                          }}
                        >
                          <b>Save Filter</b>
                        </Button>
                      </AnimateButton>
                    </Box>
                  </Grid>
                </Grid>
              </MDBox>
            </Fade>
          </Modal>
        </Container>
      </MDBox>
    </>
  );
};

export default RentalItemFilterModal;
